import { useCallback, useEffect, useRef } from 'react';
import {
  PLAYBACK_STATUS,
  STARTUP_PLAY_DELAY_MS,
  WATCHTIME_CUTOFF_WARNING_MS,
} from '../config/videoPlayer.config';
import useReadyVideoProgress from './useReadyVideoProgress';
import useVideoAudioSelection from './useVideoAudioSelection';
import useVideoFitMode from './useVideoFitMode';
import useVideoFullscreen from './useVideoFullscreen';
import useVideoPlayAttempt from './useVideoPlayAttempt';
import useVideoPlaybackActions from './useVideoPlaybackActions';
import useVideoPlaybackRequest from './useVideoPlaybackRequest';
import useVideoSourceLifecycle from './useVideoSourceLifecycle';
import useVideoStartup from './useVideoStartup';
import useVideoStreamRecovery from './useVideoStreamRecovery';
import useVideoStreamSource from './useVideoStreamSource';
import useWatchTimeLimit from './useWatchTimeLimit';

/**
 * Connects the VideoPlayer hooks and exposes the values and actions used by the UI.
 *
 * This hook owns the video element ref and coordinates starting, pausing, resuming,
 * stopping, restarting, recovery, watch-time limits, audio, fit mode, loaded-video
 * progress, stream setup, and video element events. The VideoPlayer component uses the
 * returned fields to render overlays, controls, and the video element. Stream setup and
 * direct video.play() calls are delegated to smaller hooks.
 */
const useVideoPlayerController = ({
  activeAudioIndex,
  autoplay,
  runtimeCutoffMs = 0,
  selfAudioIndex,
  setActiveAudioIndex,
  src,
  widgetRuntime,
}) => {
  const videoRef = useRef(null); // Ref to the HTML <video> element
  const playRef = useRef(null); // Needed to allow playAfterDelay to call the play function from useVideoPlaybackActions
  const retryAttemptRef = useRef(0); // Tracks how many times the player has tried to recover from a playback problem
  const playAfterVideoElementRemountsRef = useRef(null); // Tracks whether the video should play after the video element remounts
  const hasUserSelectedPlaybackPositionRef = useRef(false); // Keeps recovery from overriding an intentional seek away from live
  const startupDelayMs = autoplay ? STARTUP_PLAY_DELAY_MS.autoplay : STARTUP_PLAY_DELAY_MS.manual;
  const watchTimeLimitEnabled = Number.isFinite(runtimeCutoffMs) && runtimeCutoffMs > 0;

  // Watch time limit hook that tracks whether the user has reached the allowed watch time and should not be allowed to start/restart playback
  const {
    accept: acceptWatchTimeLimit,
    getIsBlocked: getIsWatchTimeLimitReached,
    isBlocked: watchTimeLimitReached,
    isWarningActive: runtimeGateWarningActive,
    pause: pauseWatchTimeLimitTimer,
    reset: resetWatchTimeLimitTimer,
    start: startWatchTimeLimitTimer,
    warningSecondsRemaining: runtimeGateWarningSecondsRemaining,
  } = useWatchTimeLimit({
    enabled: watchTimeLimitEnabled,
    limitMs: runtimeCutoffMs,
    warningMs: WATCHTIME_CUTOFF_WARNING_MS,
  });

  // Hook that tracks the playback status (hasActiveStream, paused, loading, etc.), and handles starting new playback requests
  const {
    playbackStatusState,
    playbackStatusRef,
    playbackViewState,
    setPlaybackStatus,
    startNewPlaybackRequest,
    isStillCurrentPlaybackRequest,
  } = useVideoPlaybackRequest();
  const { loading, paused, hasActiveStream } = playbackViewState;
  const controlsDisabled =
    watchTimeLimitReached || playbackStatusState === PLAYBACK_STATUS.starting;

  // Hook that safely calls video.play() and handles any errors that may occur, including ignoring AbortError failures and logging real play failures
  const { playVideo: tryToPlayVideo } = useVideoPlayAttempt({
    isCurrentRequest: isStillCurrentPlaybackRequest,
  });

  // Hook that tracks the video fit mode and provides a function to cycle through the available fit modes
  const { fitMode, fitLabel, fitDescription, showNextFitMode } = useVideoFitMode();

  // Hook that tracks the ready video progress (progress, bufferedProgress, etc.) and provides functions to manipulate it
  const {
    progress,
    bufferedProgress,
    bufferedSecondsAhead,
    resetReadyVideoProgress,
    updateReadyVideoProgress,
    skipToNewestReadyVideo,
    seekToProgress,
  } = useReadyVideoProgress({ videoRef });

  const clearUserSelectedPlaybackPosition = useCallback(() => {
    hasUserSelectedPlaybackPositionRef.current = false;
  }, []);

  const setUserSelectedPlaybackPosition = useCallback(() => {
    hasUserSelectedPlaybackPositionRef.current = true;
  }, []);

  // Hook that handles the video stream source (attaching, clearing, and checking if a stream is attached)
  const { attachStream, clearStream, hasAttachedStream } = useVideoStreamSource({
    isCurrentRequest: isStillCurrentPlaybackRequest,
    updateReadyVideoProgress,
  });

  // Hook that owns the high-level reset/remount/release lifecycle for the current video source
  const { mediaElementKey, releaseStream, resetVideoPlayer } = useVideoSourceLifecycle({
    clearStream,
    pauseWatchTimeLimitTimer,
    resetReadyVideoProgress,
    resetWatchTimeLimitTimer,
    src,
    startNewPlaybackRequest,
    videoRef,
  });

  // Hook that handles audio selection (muted/unmuted, volume) for the video element
  const { muted, volume, toggleMuted, changeVolumeFromSlider } = useVideoAudioSelection({
    unmutedVideoIndex: activeAudioIndex,
    videoIndex: selfAudioIndex,
    setUnmutedVideoIndex: setActiveAudioIndex,
    videoElementKey: mediaElementKey, // Signals when the video element has been reset
    hasActiveStream,
    videoRef,
  });

  // Lets recovery timers request the latest play action after a delay.
  const playAfterDelay = useCallback(
    (extraDelayMs = 0) => {
      playRef.current?.(startupDelayMs + extraDelayMs);
    },
    [startupDelayMs],
  );

  const { cancelPendingRecovery, restartStreamAfterPlaybackProblem, recoverFromPlaybackStall } =
    useVideoStreamRecovery({
      isStillCurrentPlaybackRequest,
      pauseWatchTimeLimitTimer,
      playbackStatusRef,
      playAfterDelay,
      retryAttemptRef,
      resetVideoPlayer,
      getIsWatchTimeLimitReached,
      setPlaybackStatus,
      skipToNewestReadyVideo,
      src,
      startNewPlaybackRequest,
      startWatchTimeLimitTimer,
      tryToPlayVideo,
      updateReadyVideoProgress,
      videoRef,
    });

  const restartStreamAfterStartupProblem = useCallback(() => {
    restartStreamAfterPlaybackProblem({
      forceStreamCacheClear: retryAttemptRef.current > 0,
    });
  }, [restartStreamAfterPlaybackProblem]);

  const { cancelPendingStartup, startPlayingWhenEnoughVideoIsReady } = useVideoStartup({
    isStillCurrentPlaybackRequest,
    playbackStatusRef,
    restartStreamAfterPlaybackProblem: restartStreamAfterStartupProblem,
    getIsWatchTimeLimitReached,
    setPlaybackStatus,
    tryToPlayVideo,
  });

  const { acceptRuntimeGate, pause, play, resetAndRestart, restart, resume, stop } =
    useVideoPlaybackActions({
      acceptWatchTimeLimit,
      attachStream,
      cancelPendingRecovery,
      cancelPendingStartup,
      hasAttachedStream,
      isStillCurrentPlaybackRequest,
      muted,
      pauseWatchTimeLimitTimer,
      playbackStatusRef,
      playAfterVideoElementRemountsRef,
      releaseStream,
      restartStreamAfterPlaybackProblem,
      resetVideoPlayer,
      retryAttemptRef,
      setPlaybackStatus,
      src,
      startNewPlaybackRequest,
      startPlayingWhenEnoughVideoIsReady,
      startWatchTimeLimitTimer,
      startupDelayMs,
      tryToPlayVideo,
      updateReadyVideoProgress,
      videoRef,
      volume,
      getIsWatchTimeLimitReached,
    });

  const playLiveStream = useCallback(
    (delayOverrideMs) => {
      clearUserSelectedPlaybackPosition();
      play(delayOverrideMs);
    },
    [clearUserSelectedPlaybackPosition, play],
  );

  const restartLiveStream = useCallback(() => {
    clearUserSelectedPlaybackPosition();
    restart();
  }, [clearUserSelectedPlaybackPosition, restart]);

  const resetAndRestartLiveStream = useCallback(() => {
    clearUserSelectedPlaybackPosition();
    resetAndRestart();
  }, [clearUserSelectedPlaybackPosition, resetAndRestart]);

  const stopPlayback = useCallback(() => {
    clearUserSelectedPlaybackPosition();
    stop();
  }, [clearUserSelectedPlaybackPosition, stop]);

  const acceptRuntimeGateAndResume = useCallback(() => {
    clearUserSelectedPlaybackPosition();
    acceptRuntimeGate();
  }, [acceptRuntimeGate, clearUserSelectedPlaybackPosition]);

  playRef.current = playLiveStream;

  const seek = useCallback(
    (nextProgress) => {
      cancelPendingRecovery();

      const didSeek = seekToProgress(nextProgress);

      if (didSeek) {
        setUserSelectedPlaybackPosition();
      }
    },
    [cancelPendingRecovery, setUserSelectedPlaybackPosition, seekToProgress],
  );

  const handlePlaying = () => {
    if (
      getIsWatchTimeLimitReached() ||
      playbackStatusRef.current === PLAYBACK_STATUS.paused ||
      playbackStatusRef.current === PLAYBACK_STATUS.idle
    ) {
      return;
    }
    cancelPendingRecovery();
    cancelPendingStartup();
    retryAttemptRef.current = 0;
    setPlaybackStatus(PLAYBACK_STATUS.playing);
    startWatchTimeLimitTimer();
    updateReadyVideoProgress();
  };

  const recoverIfPlaybackIsActive = useCallback(
    (reason, options) => {
      if (playbackStatusRef.current !== PLAYBACK_STATUS.playing) return;
      if (hasUserSelectedPlaybackPositionRef.current) {
        updateReadyVideoProgress();
        return;
      }

      recoverFromPlaybackStall(reason, options);
    },
    [playbackStatusRef, recoverFromPlaybackStall, updateReadyVideoProgress],
  );

  const videoEventHandlers = {
    onCanPlay: updateReadyVideoProgress,
    onEnded: () =>
      recoverIfPlaybackIsActive('ended', {
        forceStreamCacheClear: retryAttemptRef.current > 0,
      }),
    onPlaying: handlePlaying,
    onProgress: updateReadyVideoProgress,
    onSeeked: updateReadyVideoProgress,
    onSeeking: updateReadyVideoProgress,
    onStalled: () => recoverIfPlaybackIsActive('stalled'),
    onTimeUpdate: updateReadyVideoProgress,
    onWaiting: () => recoverIfPlaybackIsActive('waiting'),
  };

  // Adds fullscreen event listeners and provides functions to enter, exit, and toggle fullscreen mode
  const { fullscreenAvailable, fullscreenRef, isFullscreen, toggleFullscreen, fullscreenLabel } =
    useVideoFullscreen(widgetRuntime);

  // Clears out the video stream and all its pending actions when the source URL changes
  useEffect(() => {
    cancelPendingRecovery();
    cancelPendingStartup();
    resetVideoPlayer({ resetWatchTime: true });

    // On unmount / src change, tell the backend this viewer is gone (releaseStream closes
    // over the src being torn down here) so it can stop transcoding if nobody else is watching.
    return () => {
      cancelPendingRecovery();
      cancelPendingStartup();
      resetVideoPlayer({ resetWatchTime: true });
      releaseStream();
    };
  }, [cancelPendingRecovery, cancelPendingStartup, releaseStream, resetVideoPlayer, src]);

  // If the watch time limit is reached, reset the video element to a clean state and remount the video element
  useEffect(() => {
    if (!watchTimeLimitReached) return;

    retryAttemptRef.current = 0;
    cancelPendingRecovery();
    cancelPendingStartup();
    resetVideoPlayer({ nextStatus: PLAYBACK_STATUS.idle, remountMedia: true });
  }, [cancelPendingRecovery, cancelPendingStartup, resetVideoPlayer, watchTimeLimitReached]);

  // Resumes the video after the video element remounts, if requested by a previous action (only happens when user accepts keep watching prompt)
  useEffect(() => {
    if (playAfterVideoElementRemountsRef.current === null) return undefined;

    const delayMs = playAfterVideoElementRemountsRef.current;
    playAfterVideoElementRemountsRef.current = null;

    // Creates a small delay waiting a full frame after the video element remounts before calling play()
    const frameId = requestAnimationFrame(() => {
      playRef.current?.(delayMs);
    });

    // Cancel the animation frame if the component unmounts or media element changes before it runs
    return () => cancelAnimationFrame(frameId);
  }, [mediaElementKey]);

  return {
    bufferedProgress,
    bufferedSecondsAhead,
    controlsDisabled,
    fitLabel,
    fitMode,
    fitDescription,
    loading,
    muted,
    paused,
    hasActiveStream,
    progress,
    watchTimeLimitReached,
    runtimeGateWarningActive,
    runtimeGateWarningSecondsRemaining,
    videoEventHandlers,
    videoRef,
    volume,
    mediaElementKey,
    acceptRuntimeGate: acceptRuntimeGateAndResume,
    cycleFitMode: showNextFitMode,
    handleVolumeSlider: changeVolumeFromSlider,
    pause,
    play: playLiveStream,
    resetAndRestart: resetAndRestartLiveStream,
    restart: restartLiveStream,
    resume,
    seek,
    stop: stopPlayback,
    toggleMute: toggleMuted,
    toggleFullscreen,
    fullscreenLabel,
    fullscreenAvailable,
    fullscreenRef,
    isFullscreen,
  };
};

export default useVideoPlayerController;
